/**
 * Custom hook for managing AdvancedStaticDynamicSelector state
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { StaticOrDynamic, TextFormat, DateFormat, NumberFormat, ColorFormat, ImageFormat, MediaType, BindingRef } from '../../../core/template.schemas';
import { EntityType } from '../../../core/template.schemas';
import { entityRegistry } from '../../registry';
import { useTemplateSlots, useTemplateStore } from '../../hooks/useTemplateStore';
import { getDefaultFormat } from '../../registry/media/media.registered';
import { getDefaultStaticValue, getEntityDisplayName } from './utils';
import { resolveTextContentWithPreview, resolveImageSource, resolveStaticOrDynamicWithPreview } from '../../../core/runtime/resolve';

interface UseAdvancedSelectorProps {
  /** Current value */
  value: StaticOrDynamic<unknown>;
  /** Change handler */
  onChange: (value: StaticOrDynamic<unknown> | null, meta?: { width?: number; height?: number }) => void;
  /** Compatible media types */
  compatibleMediaTypes: MediaType[];
}

/**
 * Hook for managing advanced selector state and logic
 */
export const useAdvancedSelector = ({
  value,
  onChange,
  compatibleMediaTypes
}: UseAdvancedSelectorProps) => {
  // Track internal changes to prevent sync loops
  const isInternalChange = useRef(false);
  const lastValueRef = useRef(value);
  
  const [mode, setMode] = useState<'static' | 'dynamic'>(value.mode);
  const [selectedMediaType, setSelectedMediaType] = useState<MediaType>(value.mediaType);
  
  const [staticValue, setStaticValue] = useState(() => {
    if (value.mode === 'static') {
      return value.staticValue as any;
    }
    return getDefaultStaticValue(value.mediaType);
  });
  
  // Placeholder-based selection state
  const [selectedSourceKind, setSelectedSourceKind] = useState<'slot' | 'selector' | 'override'>(() => {
    if (value.mode === 'dynamic') {
      const k = (value.dynamic.source as any)?.kind;
      if (k === 'selector') return 'selector';
      if (k === 'override') return 'override';
      return 'slot';
    }
    return 'slot';
  });
  const [selectedSlotId, setSelectedSlotId] = useState<string>(() => {
    if (value.mode === 'dynamic' && value.dynamic.source.kind === 'slot') {
      return value.dynamic.source.slotId;
    }
    return '';
  });
  
  const [selectedSlotAttributePath, setSelectedSlotAttributePath] = useState<string>(() => {
    if (value.mode === 'dynamic' && value.dynamic.source.kind === 'slot') {
      return value.dynamic.source.attributePath;
    }
    return '';
  });

  const [selectedSelectorId, setSelectedSelectorId] = useState<string>(() => {
    if (value.mode === 'dynamic' && (value.dynamic.source as any).kind === 'selector') {
      return (value.dynamic.source as any).selectorId || '';
    }
    return '';
  });

  const [selectedOverrideKey, setSelectedOverrideKey] = useState<string>(() => {
    if (value.mode === 'dynamic' && (value.dynamic.source as any).kind === 'override') {
      return (value.dynamic.source as any).overrideKey || '';
    }
    return '';
  });
  
  // Initialize format with default if not provided
  const [format, setFormat] = useState<TextFormat | DateFormat | NumberFormat | ColorFormat | ImageFormat>(() => {
    if (value.format) {
      return value.format;
    }
    return getDefaultFormat(value.mediaType);
  });

  // Get placeholders and store functions
  const allSlots = useTemplateSlots();
  const addSlot = useTemplateStore(s => s.addSlot);
  const templateSlots = allSlots;

  /**
   * Helper function to find the first compatible placeholder and attribute
   */
  const findFirstCompatibleSlotAndAttribute = useCallback(() => {
    const compatibleSlots = allSlots.filter(slot => {
      const entity = entityRegistry.get(slot.entityType);
      return entity?.attributes.some(attr => compatibleMediaTypes.includes(attr.mediaType));
    });
    
    for (const slot of compatibleSlots) {
      const entity = entityRegistry.get(slot.entityType);
      const compatibleAttribute = entity?.attributes.find(attr => 
        compatibleMediaTypes.includes(attr.mediaType)
      );
      
      if (compatibleAttribute) {
        return {
          slot,
          attribute: compatibleAttribute
        };
      }
    }
    
    return null;
  }, [allSlots, compatibleMediaTypes]);

  // Deep comparison utility
  const isEqual = (a: any, b: any) => {
    if (a === b) return true;
    if (a && b && typeof a === 'object' && typeof b === 'object') {
      if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) return false;
        for (let i = 0; i < a.length; i++) {
          if (!isEqual(a[i], b[i])) return false;
        }
        return true;
      }
      if (Object.keys(a).length !== Object.keys(b).length) return false;
      for (const key in a) {
        if (!Object.prototype.hasOwnProperty.call(b, key) || !isEqual(a[key], b[key])) {
          return false;
        }
      }
      return true;
    }
    return false;
  };

  // Sync with external value changes (only when value actually changes)
  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    
    // Check if the value has actually changed using deep comparison
    if (isEqual(lastValueRef.current, value)) return;
    
    lastValueRef.current = value;
    
    setMode(value.mode);
    setSelectedMediaType(value.mediaType);
    
    if (value.mode === 'static') {
      setStaticValue(value.staticValue);
      setFormat(value.format || getDefaultFormat(value.mediaType));
    } else if (value.mode === 'dynamic') {
      const source = value.dynamic.source;
      if (source.kind === 'slot') {
        setSelectedSourceKind('slot');
        setSelectedSlotId(source.slotId);
        setSelectedSlotAttributePath(source.attributePath);
        setSelectedSelectorId('');
      } else if (source.kind === 'selector') {
        setSelectedSourceKind('selector');
        setSelectedSelectorId(source.selectorId || '');
        setSelectedSlotId('');
        setSelectedSlotAttributePath('');
      } else if (source.kind === 'override') {
        setSelectedSourceKind('override');
        setSelectedOverrideKey(source.overrideKey || '');
        setSelectedSlotId('');
        setSelectedSlotAttributePath('');
        setSelectedSelectorId('');
      }
      if (value.format) {
        setFormat(value.format);
      } else {
        setFormat(getDefaultFormat(value.mediaType));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Debounced onChange to prevent multiple rapid calls
  const debouncedOnChange = useCallback((newValue: StaticOrDynamic<unknown>, meta?: { width?: number; height?: number }) => {
    isInternalChange.current = true;
    lastValueRef.current = newValue;
    
    // Use setTimeout to batch multiple rapid changes
    setTimeout(() => {
      onChange(newValue, meta);
    }, 0);
  }, [onChange]);

  // Handler functions
  const handleModeChange = (newMode: 'static' | 'dynamic') => {
    setMode(newMode);
    
    if (newMode === 'static') {
      // Switching from Dynamic to Static
      // Use the defaultValue from dynamic config if available, otherwise fallback to resolved preview or default
      let newStaticValue: any = getDefaultStaticValue(selectedMediaType);
      
      if (lastValueRef.current?.mode === 'dynamic') {
        const dyn = lastValueRef.current.dynamic;
        if (dyn.defaultValue !== undefined && dyn.defaultValue !== null) {
          newStaticValue = dyn.defaultValue;
        } else if (defaultDisplayValue !== undefined) {
           // Use resolved preview value if available and valid
           // defaultDisplayValue might be an object for images { path: ... } or string
           if (selectedMediaType === 'image' && typeof defaultDisplayValue === 'object') {
             newStaticValue = defaultDisplayValue;
           } else if (['text', 'date', 'int', 'float', 'color'].includes(selectedMediaType)) {
             newStaticValue = defaultDisplayValue;
           }
        }
      }

      setStaticValue(newStaticValue);

      debouncedOnChange({
        mode: 'static',
        mediaType: selectedMediaType,
        staticValue: newStaticValue,
        format: format // Preserve format
      });
    } else {
      // Switching from Static to Dynamic
      // Use the current staticValue as the defaultValue for dynamic config
      
      const currentStaticValue = staticValue;

      // Only auto-select if no placeholder is currently selected
      if (selectedSourceKind === 'slot' && (!selectedSlotId || !selectedSlotAttributePath)) {
        const compatible = findFirstCompatibleSlotAndAttribute();
        if (compatible) {
          setSelectedSlotId(compatible.slot.id);
          setSelectedSlotAttributePath(compatible.attribute.attributeKey);
          setSelectedMediaType(compatible.attribute.mediaType);
          
          const defaultFormat = getDefaultFormat(compatible.attribute.mediaType);
          // Prefer existing format if compatible, otherwise use default
          const nextFormat = format || defaultFormat;
          setFormat(nextFormat);
          
          debouncedOnChange({
            mode: 'dynamic',
            mediaType: compatible.attribute.mediaType,
            format: nextFormat,
            dynamic: {
              source: {
                kind: 'slot',
                slotId: compatible.slot.id,
                attributePath: compatible.attribute.attributeKey
              },
              defaultValue: currentStaticValue // Set static value as default
            }
          });
        }
      } else {
        // Use existing selection if valid
        let source: BindingRef | null = null;
        
        if (selectedSourceKind === 'selector') {
          if (selectedSelectorId && selectedSelectorId.trim() !== '') {
            source = { kind: 'selector', selectorId: selectedSelectorId };
          }
        } else if (selectedSourceKind === 'override') {
          if (selectedOverrideKey && selectedOverrideKey.trim() !== '') {
            source = { kind: 'override', overrideKey: selectedOverrideKey };
          }
        } else {
          // Slot
          if (selectedSlotId && selectedSlotAttributePath) {
            source = { kind: 'slot', slotId: selectedSlotId, attributePath: selectedSlotAttributePath };
          }
        }
          
        if (source) {
          debouncedOnChange({
            mode: 'dynamic',
            mediaType: selectedMediaType,
            format: format, // Preserve format
            dynamic: {
              source,
              defaultValue: currentStaticValue // Set static value as default
            }
          });
        }
      }
    }
  };

  // Helper to get current default value
  const getCurrentDefaultValue = () => {
    if (lastValueRef.current?.mode === 'dynamic') {
      return lastValueRef.current.dynamic.defaultValue;
    }
    return undefined;
  };

  const handleSlotSelect = (slotId: string) => {
    setSelectedSourceKind('slot');
    setSelectedSlotId(slotId);
    
    const slot = allSlots.find(s => s.id === slotId);
    if (!slot) return;
    
    const entity = entityRegistry.get(slot.entityType);
    
    // Prefer attribute matching current media type
    let compatibleAttribute = entity?.attributes.find(attr => attr.mediaType === selectedMediaType);
    
    // Fallback to any compatible attribute
    if (!compatibleAttribute) {
      compatibleAttribute = entity?.attributes.find(attr => 
        compatibleMediaTypes.includes(attr.mediaType)
      );
    }
    
    if (compatibleAttribute) {
      const isMediaTypeChanged = compatibleAttribute.mediaType !== selectedMediaType;
      setSelectedSlotAttributePath(compatibleAttribute.attributeKey);
      setSelectedMediaType(compatibleAttribute.mediaType);
      
      const defaultFormat = getDefaultFormat(compatibleAttribute.mediaType);
      setFormat(defaultFormat);
      
      debouncedOnChange({
        mode: 'dynamic',
        mediaType: compatibleAttribute.mediaType,
        format: defaultFormat,
        dynamic: {
          source: {
            kind: 'slot',
            slotId: slotId,
            attributePath: compatibleAttribute.attributeKey
          },
          defaultValue: isMediaTypeChanged ? undefined : getCurrentDefaultValue()
        }
      });
    } else {
      // Clear selection if no compatible attributes
      setSelectedSlotAttributePath('');
    }
  };

  const handleStaticValueChange = (newValue: any, meta?: { width?: number; height?: number }) => {
    setStaticValue(newValue);
    
    debouncedOnChange({
      mode: 'static',
      mediaType: selectedMediaType,
      staticValue: newValue,
      format
    }, meta);
  };

  const handleSlotAttributeSelect = (attributePath: string, attributeMediaType: MediaType) => {
    // Only allow selection if the attribute media type is compatible
    if (!compatibleMediaTypes.includes(attributeMediaType)) {
      console.warn(`Attribute media type ${attributeMediaType} is not compatible with ${compatibleMediaTypes.join(', ')}`);
      return;
    }
    
    const isMediaTypeChanged = attributeMediaType !== selectedMediaType;
    setSelectedSlotAttributePath(attributePath);
    setSelectedMediaType(attributeMediaType);
    
    const defaultFormat = getDefaultFormat(attributeMediaType);
    setFormat(defaultFormat);
    
    debouncedOnChange({
      mode: 'dynamic',
      mediaType: attributeMediaType,
      format: defaultFormat,
      dynamic: {
        source: { kind: 'slot', slotId: selectedSlotId, attributePath },
        defaultValue: isMediaTypeChanged ? undefined : getCurrentDefaultValue()
      }
    });
  };

  const handleFormatChange = (newFormat: any) => {
    setFormat(newFormat);
    
    if (mode === 'static') {
      debouncedOnChange({
        mode: 'static',
        mediaType: selectedMediaType,
        staticValue,
        format: newFormat,
      });
    } else if (mode === 'dynamic') {
      let source: BindingRef | null = null;
      if (selectedSourceKind === 'selector' && selectedSelectorId) {
        source = { kind: 'selector', selectorId: selectedSelectorId };
      } else if (selectedSourceKind === 'override' && selectedOverrideKey) {
        source = { kind: 'override', overrideKey: selectedOverrideKey };
      } else if (selectedSlotId && selectedSlotAttributePath) {
        source = { kind: 'slot', slotId: selectedSlotId, attributePath: selectedSlotAttributePath };
      }

      if (source) {
        debouncedOnChange({
          mode: 'dynamic',
          mediaType: selectedMediaType,
          format: newFormat,
          dynamic: {
            source,
            defaultValue: getCurrentDefaultValue()
          }
        });
      }
    }
  };

  const handleCreateSlot = (entityType: EntityType) => {
    const slotName = `${getEntityDisplayName(entityType)} ${allSlots.filter(s => s.entityType === entityType).length + 1}`;

    addSlot({
      name: slotName,
      entityType: entityType,
      description: `Automatisch erstellter ${getEntityDisplayName(entityType)}-Platzhalter`
    });
  };

  const handleMediaTypeChange = (mediaType: MediaType) => {
    // Only allow media type changes that are compatible
    if (!compatibleMediaTypes.includes(mediaType)) {
      console.warn(`Media type ${mediaType} is not compatible with ${compatibleMediaTypes.join(', ')}`);
      return;
    }
    
    setSelectedMediaType(mediaType);
    
    const newStaticValue = getDefaultStaticValue(mediaType);
    setStaticValue(newStaticValue);
    
    const newFormat = getDefaultFormat(mediaType);
    setFormat(newFormat);
    
    if (mode === 'static') {
      debouncedOnChange({
        mode: 'static',
        mediaType: mediaType,
        staticValue: newStaticValue,
        format: newFormat,
      });
    } else if (mode === 'dynamic') {
      // In dynamic mode, if we change media type, the old default value is likely invalid.
      // We should reset it to undefined (or a valid default for the new type).
      // Also we need to find a compatible slot attribute.
      
      const slot = selectedSlotId ? allSlots.find(s => s.id === selectedSlotId) : null;
      
      if (slot) {
        const entity = entityRegistry.get(slot.entityType);
        const compatibleAttribute = entity?.attributes.find(attr => attr.mediaType === mediaType);
        
        if (compatibleAttribute) {
          setSelectedSlotAttributePath(compatibleAttribute.attributeKey);
          debouncedOnChange({
            mode: 'dynamic',
            mediaType: mediaType,
            format: newFormat,
            dynamic: {
              source: {
                kind: 'slot',
                slotId: selectedSlotId,
                attributePath: compatibleAttribute.attributeKey
              },
              defaultValue: undefined // Reset default value on type change
            }
          });
          return;
        }
      }
      
      // If current placeholder doesn't have the media type, find another placeholder
      let compatible: { slot: any; attribute: any } | null = null;
      
      // Search for a slot that specifically supports the new media type
      for (const s of allSlots) {
        const entity = entityRegistry.get(s.entityType);
        const attr = entity?.attributes.find(a => a.mediaType === mediaType);
        if (attr) {
          compatible = { slot: s, attribute: attr };
          break;
        }
      }

      if (compatible) {
        setSelectedSlotId(compatible.slot.id);
        setSelectedSlotAttributePath(compatible.attribute.attributeKey);
        debouncedOnChange({
          mode: 'dynamic',
          mediaType: mediaType,
          format: newFormat,
          dynamic: {
            source: {
              kind: 'slot',
              slotId: compatible.slot.id,
              attributePath: compatible.attribute.attributeKey
            },
            defaultValue: undefined // Reset default value on type change
          }
        });
      } else {
        // No compatible slot found.
        // We must NOT emit an invalid change.
        // We clear the selection locally so the UI reflects the need to select a slot.
        setSelectedSlotId('');
        setSelectedSlotAttributePath('');
        // We do NOT call debouncedOnChange.
        // The component enters a "draft" state where local mediaType is updated but value is not committed.
      }
    }
  };

  const defaultDisplayValue = useMemo(() => {
    if (value.mode !== 'dynamic') return undefined;
    const ctx = { selected: {}, overrides: {}, templateSlots } as any;
    if (['text', 'date', 'int', 'float'].includes(value.mediaType)) {
      return resolveTextContentWithPreview(value as any, ctx);
    }
    if (value.mediaType === 'image') {
      const path = resolveImageSource(value as any, ctx);
      return { path };
    }
    return resolveStaticOrDynamicWithPreview(value as any, ctx);
  }, [value, templateSlots]);

  const handleDefaultValueChange = (newDefault: any, meta?: { width?: number; height?: number }) => {
    if (mode !== 'dynamic') return;
    
    let source: BindingRef | undefined = (lastValueRef.current?.mode === 'dynamic') ? lastValueRef.current.dynamic.source : undefined;
    
    if (!source) {
       // Construct from local state
       if (selectedSourceKind === 'selector' && selectedSelectorId) {
           source = { kind: 'selector', selectorId: selectedSelectorId };
       } else if (selectedSourceKind === 'override' && selectedOverrideKey) {
           source = { kind: 'override', overrideKey: selectedOverrideKey };
       } else if (selectedSlotId && selectedSlotAttributePath) {
           source = { kind: 'slot', slotId: selectedSlotId, attributePath: selectedSlotAttributePath };
       }
    }
    
    if (!source) return;

    const currentDynamic = lastValueRef.current?.mode === 'dynamic' ? lastValueRef.current.dynamic : undefined;

    debouncedOnChange({
      mode: 'dynamic',
      mediaType: selectedMediaType,
      format,
      dynamic: {
        source,
        defaultValue: newDefault,
        label: currentDynamic?.label,
        description: currentDynamic?.description,
      }
    }, meta);
  };

  const handleClearDefaultValue = () => {
    if (mode !== 'dynamic') return;
    
    let source: BindingRef | undefined = (lastValueRef.current?.mode === 'dynamic') ? lastValueRef.current.dynamic.source : undefined;
    
    if (!source) {
       // Construct from local state
       if (selectedSourceKind === 'selector' && selectedSelectorId) {
           source = { kind: 'selector', selectorId: selectedSelectorId };
       } else if (selectedSourceKind === 'override' && selectedOverrideKey) {
           source = { kind: 'override', overrideKey: selectedOverrideKey };
       } else if (selectedSlotId && selectedSlotAttributePath) {
           source = { kind: 'slot', slotId: selectedSlotId, attributePath: selectedSlotAttributePath };
       }
    }
    
    if (!source) return;

    const currentDynamic = lastValueRef.current?.mode === 'dynamic' ? lastValueRef.current.dynamic : undefined;

    debouncedOnChange({
      mode: 'dynamic',
      mediaType: selectedMediaType,
      format,
      dynamic: {
        source,
        defaultValue: undefined,
        label: currentDynamic?.label,
        description: currentDynamic?.description,
      }
    }, undefined);
  };

  const handleSourceKindChange = (kind: 'slot' | 'selector' | 'override') => {
    setSelectedSourceKind(kind);
    if (mode === 'dynamic') {
      let source: BindingRef;
      
      // Validation: Check if the target kind has valid data before switching/saving
      // If not, we might still want to switch the UI (setSelectedSourceKind above) 
      // but NOT propagate the change to the parent if it results in an invalid state.
      // However, usually switching kind implies we want to start editing that kind.
      // If we don't propagate, the parent keeps the old kind.
      // But the UI shows the new kind. This is what we want (UI separate from valid state).
      
      if (kind === 'selector') {
        if (!selectedSelectorId || selectedSelectorId.trim() === '') return;
        source = { kind: 'selector', selectorId: selectedSelectorId };
      } else if (kind === 'override') {
        if (!selectedOverrideKey || selectedOverrideKey.trim() === '') return;
        source = { kind: 'override', overrideKey: selectedOverrideKey };
      } else {
        if (!selectedSlotId) return;
        // Ensure attributePath is valid
        if (!selectedSlotAttributePath) return;
        source = { kind: 'slot', slotId: selectedSlotId, attributePath: selectedSlotAttributePath };
      }
      
      debouncedOnChange({
        mode: 'dynamic',
        mediaType: selectedMediaType,
        format,
        dynamic: { 
          source,
          defaultValue: getCurrentDefaultValue()
        }
      });
    }
  };

  const handleSelectorIdChange = (selectorId: string) => {
    setSelectedSelectorId(selectorId);
    setSelectedSourceKind('selector');
    if (mode === 'dynamic') {
      if (!selectorId || selectorId.trim() === '') return;
      
      debouncedOnChange({
        mode: 'dynamic',
        mediaType: selectedMediaType,
        format,
        dynamic: { 
          source: { kind: 'selector', selectorId },
          defaultValue: getCurrentDefaultValue()
        }
      });
    }
  };

  const handleOverrideKeyChange = (key: string) => {
    setSelectedOverrideKey(key);
    setSelectedSourceKind('override');
    if (mode === 'dynamic') {
      if (!key || key.trim() === '') return;

      debouncedOnChange({
        mode: 'dynamic',
        mediaType: selectedMediaType,
        format,
        dynamic: { 
          source: { kind: 'override', overrideKey: key },
          defaultValue: getCurrentDefaultValue()
        }
      });
    }
  };

  return {
    // State
    mode,
    selectedMediaType,
    staticValue,
    selectedSourceKind,
    selectedSlotId,
    selectedSlotAttributePath,
    selectedSelectorId,
    selectedOverrideKey,
    format,
    defaultDisplayValue,
    
    // Handlers
    handleModeChange,
    handleSlotSelect,
    handleStaticValueChange,
    handleSlotAttributeSelect,
    handleFormatChange,
    handleCreateSlot,
    handleMediaTypeChange,
    handleDefaultValueChange,
    handleClearDefaultValue,
    handleSourceKindChange,
    handleSelectorIdChange,
    handleOverrideKeyChange
  };
};
